import React, { useEffect, useRef } from 'react';
import { isLand } from '../utils/landMask';

/**
 * A slowly turning wireframe globe, drawn on a 2D canvas, that sits behind the
 * project cards and drifts with the pointer.
 *
 * ── Why not three.js ──────────────────────────────────────────────────────
 * A WebGL globe is ~150 KB gzipped and this renders on the homepage, which is
 * the one bundle this project works hardest to keep small. Everything here is
 * an orthographic projection of points on a sphere — a handful of sin/cos per
 * point — so it costs nothing but the file you are reading.
 *
 * ── Why it stays quiet ────────────────────────────────────────────────────
 * The cards on top must stay legible, so the globe is deliberately restrained:
 * it never draws at full strength, the far hemisphere fades toward nothing,
 * and the whole canvas is blurred and dimmed in CSS. It is texture behind
 * glass, not a picture competing with the content.
 *
 * ── Cost control ──────────────────────────────────────────────────────────
 * The loop runs only while the canvas is actually on screen and the tab is
 * visible. Device pixel ratio is capped at 2 — beyond that it is invisible
 * work. prefers-reduced-motion draws a single static frame and stops.
 */

const CANDIDATES = 24000; // scattered over the whole sphere; ~a third survive
const PARALLELS = 9;      // lines of latitude
const MERIDIANS = 18;     // lines of longitude
const ARC_STEPS = 84;     // samples per line
const DEG = 180 / Math.PI;

/**
 * Scatters candidates evenly over the sphere with the Fibonacci spiral (a
 * lat/lon grid clumps badly at the poles), then keeps only the ones that fall
 * on land. What survives is the real coastline of every continent.
 */
function buildLandPoints() {
    const keep = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < CANDIDATES; i++) {
        const y = 1 - (i / (CANDIDATES - 1)) * 2;
        const r = Math.sqrt(Math.max(0, 1 - y * y));
        const theta = golden * i;
        const x = Math.cos(theta) * r;
        const z = Math.sin(theta) * r;
        // Unit vector -> geographic coordinates.
        const lat = Math.asin(y) * DEG;
        const lon = Math.atan2(z, x) * DEG;
        if (isLand(lat, lon)) keep.push(x, y, z);
    }
    return new Float32Array(keep);
}

// Latitude rings + longitude half-arcs, as flat point arrays.
function buildGraticule() {
    const lines = [];
    for (let p = 1; p < PARALLELS; p++) {
        const phi = (p / PARALLELS) * Math.PI - Math.PI / 2;
        const y = Math.sin(phi);
        const r = Math.cos(phi);
        const arc = new Float32Array(ARC_STEPS * 3);
        for (let s = 0; s < ARC_STEPS; s++) {
            const t = (s / (ARC_STEPS - 1)) * Math.PI * 2;
            arc[s * 3] = Math.cos(t) * r;
            arc[s * 3 + 1] = y;
            arc[s * 3 + 2] = Math.sin(t) * r;
        }
        lines.push(arc);
    }
    for (let m = 0; m < MERIDIANS; m++) {
        const lon = (m / MERIDIANS) * Math.PI * 2;
        const arc = new Float32Array(ARC_STEPS * 3);
        for (let s = 0; s < ARC_STEPS; s++) {
            const phi = (s / (ARC_STEPS - 1)) * Math.PI - Math.PI / 2;
            const r = Math.cos(phi);
            arc[s * 3] = Math.cos(lon) * r;
            arc[s * 3 + 1] = Math.sin(phi);
            arc[s * 3 + 2] = Math.sin(lon) * r;
        }
        lines.push(arc);
    }
    return lines;
}

/**
 * @param {'follow'|'drag'} mode  'follow' drifts after the pointer;
 *                                'drag' is picked up and placed by hand.
 */
const GlobeBackdrop = ({ className = '', mode = 'follow' }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isDrag = mode === 'drag';

        const points = buildLandPoints();
        const dotCount = points.length / 3;
        // x, y, depth per point — reused every frame, never reallocated.
        const scratch = new Float32Array(dotCount * 3);
        const BANDS = 6;
        const graticule = buildGraticule();

        // Declared before resize(), which assigns them. Leaving these below the
        // resize() call put them in the temporal dead zone and threw on mount.
        let maxX = 0, maxY = 0;         // clamped so it never leaves the view
        let width = 0;
        let height = 0;
        let radius = 0;

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            width = rect.width;
            height = rect.height;
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            // 0.62 put the sphere's DIAMETER at 1.24x the canvas, so it always
            // overflowed — masked and unnoticed on the homepage, visibly cut off
            // here. Drag mode keeps the whole globe inside with room for the dot
            // radius and the glow; follow mode still bleeds past its mask on
            // purpose, to read as a horizon rather than a small ball.
            radius = Math.min(width, height) * (isDrag ? 0.38 : 0.62);
            // 0.38 is chosen so the atmosphere, drawn out to radius * 1.25,
            // also lands inside the canvas (0.38 * 1.25 = 0.475 < 0.5).
            // Otherwise the glow clips into a square even though the
            // sphere itself fits.

            // How far it may roam and still be wholly on screen. Without this
            // a hard flick to a corner threw the globe out of view entirely,
            // which is the opposite of the point.
            const stage = canvas.closest('.globe-stage');
            const stageW = stage ? stage.clientWidth : window.innerWidth;
            maxX = Math.max(0, (stageW - width) / 2);
            maxY = Math.max(0, (window.innerHeight - height) / 2 - 16);
        };
        resize();

        // Pointer parallax. Targets are eased toward, never snapped, so a fast
        // flick across the screen does not make the globe jump.
        let targetYaw = 0, yaw = 0;
        let targetPitch = 0, pitch = 0;
        let targetX = 0, offsetX = 0;   // px the globe slides toward the pointer
        let targetY = 0, offsetY = 0;
        let spin = 0;

        // How far the globe roams, as a share of the viewport. Large on
        // purpose: this is what lets it travel behind different cards
        // instead of hovering near the middle.
        const SHIFT_X = 0.30;
        const SHIFT_Y = 0.28;

        const onPointer = (e) => {
            const rect = canvas.getBoundingClientRect();
            // Normalised against the VIEWPORT, not the canvas: the pointer is
            // usually outside the canvas box, and measuring against the box
            // made the response explode once you left it.
            let nx = (e.clientX - (rect.left + rect.width / 2)) / window.innerWidth;
            // Measured against a half-viewport so vertical travel is as
            // responsive as horizontal — the full height made ny tiny for any
            // normal mouse movement, so up/down barely registered.
            let ny = (e.clientY - (rect.top + rect.height / 2)) / (window.innerHeight * 0.6);
            nx = Math.max(-1, Math.min(1, nx * 2));
            ny = Math.max(-1, Math.min(1, ny * 2));

            // Pointer left, globe goes left. Same sign, no tricks — then held
            // inside the limits above so it stays fully visible at any extreme.
            const wantX = nx * window.innerWidth * SHIFT_X;
            const wantY = ny * window.innerHeight * SHIFT_Y;
            targetX = Math.max(-maxX, Math.min(maxX, wantX));
            targetY = Math.max(-maxY, Math.min(maxY, wantY));

            // A little rotation on top, so it turns to face you as it moves
            // rather than sliding like a flat sticker.
            targetYaw = nx * 0.42;
            targetPitch = -ny * 0.3;
        };

        // ── Drag mode: a trackball ─────────────────────────────────────
        // The globe stays exactly where it is parked. Dragging turns it, the
        // way you would spin a desk globe: horizontal drag changes longitude,
        // vertical drag tilts the poles. Let go and the throw carries on,
        // decaying back into the steady drift it had before you touched it.
        let dragging = false;
        let lastX = 0, lastY = 0;
        let flingYaw = 0;              // leftover spin after release

        // Pitch stops short of the poles: past that the globe rolls over and
        // the continents read upside down.
        const PITCH_LIMIT = 1.05;
        // Screen pixels -> radians. A full drag across the globe turns it
        // roughly once, which is what makes it feel like a physical object.
        const TURN = 0.009;

        const onDown = (e) => {
            if (!isDrag) return;
            dragging = true;
            lastX = e.clientX;
            lastY = e.clientY;
            flingYaw = 0;
            canvas.classList.add('is-dragging');
            // Keeps the drag alive once the pointer leaves the canvas — which
            // happens immediately on any quick spin.
            try { canvas.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
        };

        const onMove = (e) => {
            if (!dragging) return;
            const dx = e.clientX - lastX;
            const dy = e.clientY - lastY;
            lastX = e.clientX;
            lastY = e.clientY;

            // Applied straight to the rotation, not eased: a trackball has to
            // track the hand exactly or it feels broken.
            yaw += dx * TURN;
            pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch + dy * TURN));
            targetYaw = yaw;
            targetPitch = pitch;
            flingYaw = dx * TURN;      // remembered for the release
        };

        const onUp = (e) => {
            if (!dragging) return;
            dragging = false;
            canvas.classList.remove('is-dragging');
            try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        };

        const project = (x, y, z) => {
            // Yaw (spin + pointer) about Y, then pitch about X.
            const cy = Math.cos(spin + yaw);
            const sy = Math.sin(spin + yaw);
            let px = x * cy - z * sy;
            let pz = x * sy + z * cy;
            const cx = Math.cos(pitch);
            const sx = Math.sin(pitch);
            const py = y * cx - pz * sx;
            pz = y * sx + pz * cx;
            return [px, py, pz];
        };

        const draw = () => {
            ctx.clearRect(0, 0, width, height);
            const cx = width / 2;
            const cy = height / 2;

            // ── Limb ───────────────────────────────────────────────────────
            // The outer circle. It is what makes the thing read as a sphere
            // rather than a loose cloud of dots.
            ctx.beginPath();
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
            ctx.lineWidth = 1;
            ctx.stroke();

            // A faint wash inside the limb so the disc has a body.
            const body = ctx.createRadialGradient(cx, cy, radius * 0.2, cx, cy, radius);
            body.addColorStop(0, 'rgba(255, 255, 255, 0.02)');
            body.addColorStop(1, 'rgba(255, 255, 255, 0.05)');
            ctx.fillStyle = body;
            ctx.beginPath();
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
            ctx.fill();

            // ── Graticule ──────────────────────────────────────────────────
            // Even, thin, white. Lines on the far side stay visible but drop
            // back, which is what gives the grid its transparency.
            ctx.lineWidth = 0.7;
            for (const arc of graticule) {
                const n = arc.length / 3;
                let prev = null;
                let prevZ = 0;
                for (let i = 0; i < n; i++) {
                    const [px, py, pz] = project(arc[i * 3], arc[i * 3 + 1], arc[i * 3 + 2]);
                    const sx = cx + px * radius;
                    const sy = cy - py * radius;
                    if (prev) {
                        const front = (pz + prevZ) / 2 > 0;
                        ctx.strokeStyle = front
                            ? 'rgba(255, 255, 255, 0.34)'
                            : 'rgba(255, 255, 255, 0.12)';
                        ctx.beginPath();
                        ctx.moveTo(prev[0], prev[1]);
                        ctx.lineTo(sx, sy);
                        ctx.stroke();
                    }
                    prev = [sx, sy];
                    prevZ = pz;
                }
            }

            // ── Land ───────────────────────────────────────────────────────
            // Front hemisphere only. Drawing the far side too turns the
            // continents into mush, because you see both halves at once.
            for (let i = 0; i < dotCount; i++) {
                const [px, py, pz] = project(points[i * 3], points[i * 3 + 1], points[i * 3 + 2]);
                scratch[i * 3] = cx + px * radius;
                scratch[i * 3 + 1] = cy - py * radius;
                scratch[i * 3 + 2] = pz;
            }

            for (let b = 0; b < BANDS; b++) {
                const lo = b / BANDS;
                const hi = (b + 1) / BANDS;
                const mid = (lo + hi) / 2;
                // Dots fade toward the limb, which reads as curvature.
                const alpha = 0.2 + mid * 0.75;
                const size = Math.max(0.42, radius * (0.0026 + mid * 0.0022));
                ctx.fillStyle = `rgba(255, 255, 255, ${alpha.toFixed(3)})`;
                ctx.beginPath();
                for (let i = 0; i < dotCount; i++) {
                    const z = scratch[i * 3 + 2];
                    if (z <= 0.02) continue;          // back-face cull
                    if (z < lo || z >= hi) continue;
                    const sx = scratch[i * 3];
                    const sy = scratch[i * 3 + 1];
                    ctx.moveTo(sx + size, sy);
                    ctx.arc(sx, sy, size, 0, Math.PI * 2);
                }
                ctx.fill();
            }
        };

        let raf = 0;
        let running = false;

        const frame = () => {
            // The steady drift, always running, in both modes.
            spin += 0.0016;

            if (isDrag) {
                if (!dragging && flingYaw !== 0) {
                    // Carry the throw, bleeding it off until it rejoins the
                    // baseline drift. No easing toward a target here — the
                    // rotation IS the state.
                    yaw += flingYaw;
                    flingYaw *= 0.94;
                    if (Math.abs(flingYaw) < 0.00008) flingYaw = 0;
                    targetYaw = yaw;
                }
                // Parked: it turns, it never travels.
            } else {
                // Eased, so the globe glides after the pointer instead of snapping.
                yaw += (targetYaw - yaw) * 0.045;
                pitch += (targetPitch - pitch) * 0.045;
                offsetX += (targetX - offsetX) * 0.06;
                offsetY += (targetY - offsetY) * 0.06;
                // translate(-50%,-50%) keeps it centred; the second translate
                // is the pointer follow.
                canvas.style.transform =
                    `translate(-50%, -50%) translate3d(${offsetX.toFixed(1)}px, ${offsetY.toFixed(1)}px, 0)`;
            }
            draw();
            raf = requestAnimationFrame(frame);
        };

        const start = () => {
            if (running || reduced) return;
            running = true;
            raf = requestAnimationFrame(frame);
        };
        const stop = () => {
            running = false;
            if (raf) cancelAnimationFrame(raf);
            raf = 0;
        };

        // Only animate while actually on screen.
        const io = new IntersectionObserver(
            ([entry]) => (entry.isIntersecting ? start() : stop()),
            { threshold: 0 }
        );
        io.observe(canvas);

        const onVisibility = () => (document.hidden ? stop() : start());
        const onResize = () => { resize(); draw(); };

        // The canvas box changes size whenever the section around it does —
        // most obviously when the project cards finish loading and the section
        // grows by a thousand pixels. Without this, the backing store kept its
        // old dimensions and the browser simply stretched the rendered globe to
        // fill the new box: it started small and then went tall.
        const ro = new ResizeObserver(() => { resize(); draw(); });
        ro.observe(canvas);

        draw();
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('resize', onResize);
        if (isDrag) {
            canvas.addEventListener('pointerdown', onDown);
            canvas.addEventListener('pointermove', onMove, { passive: true });
            canvas.addEventListener('pointerup', onUp);
            canvas.addEventListener('pointercancel', onUp);
        } else if (!reduced) {
            window.addEventListener('pointermove', onPointer, { passive: true });
        }

        return () => {
            stop();
            ro.disconnect();
            io.disconnect();
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('pointermove', onPointer);
            canvas.removeEventListener('pointerdown', onDown);
            canvas.removeEventListener('pointermove', onMove);
            canvas.removeEventListener('pointerup', onUp);
            canvas.removeEventListener('pointercancel', onUp);
        };
    }, [mode]);

    return (
        <div className={`globe-sticky ${mode === 'drag' ? 'is-drag' : ''}`.trim()} aria-hidden="true">
            <canvas ref={canvasRef} className={`globe-backdrop ${className}`.trim()} />
        </div>
    );
};

export default GlobeBackdrop;
